/**
 * Stay-vs-move comparison (spec §9): when the plan includes a relocation,
 * show the lifetime tax and ending-net-worth difference against staying put.
 */
import { useMemo } from 'react';
import type { Plan } from '../model/types';
import { runProjection } from '../engine/projection';
import { fmtUsd, fmtCompactUsd } from './format';

interface Props {
  plan: Plan;
}

export function RelocationCompare({ plan }: Props) {
  const compare = useMemo(() => {
    if (plan.household.relocations.length === 0) return null;
    const stay: Plan = structuredClone(plan);
    stay.household.relocations = [];
    const withMove = runProjection(plan, 2026);
    const staying = runProjection(stay, 2026);
    const lifetimeTax = (r: typeof withMove) =>
      r.snapshots.reduce((t, s) => t + s.totalTaxLiability, 0);
    const stateTaxOnly = (r: typeof withMove) =>
      r.snapshots.reduce((t, s) => t + s.stateTax, 0);
    const endNW = (r: typeof withMove) => r.snapshots[r.snapshots.length - 1]?.netWorth ?? 0;
    return {
      move: plan.household.relocations[0]!,
      taxDelta: lifetimeTax(staying) - lifetimeTax(withMove),
      stateTaxDelta: stateTaxOnly(staying) - stateTaxOnly(withMove),
      nwDelta: endNW(withMove) - endNW(staying),
    };
  }, [plan]);

  if (!compare) return null;

  const { move, taxDelta, stateTaxDelta, nwDelta } = compare;
  const saves = taxDelta >= 0;

  return (
    <section className="card" aria-label="Relocation comparison">
      <h2 className="section-title">
        Moving to {move.toState} in {move.year} vs. staying
        <span className="note">lifetime totals, nominal dollars</span>
      </h2>
      <div className="hero-row">
        <div className="stat">
          <div className="label">Lifetime tax {saves ? 'saved' : 'added'} by moving</div>
          <div className={`value${saves ? '' : ' negative'}`}>{fmtCompactUsd(Math.abs(taxDelta))}</div>
          <div className="context">state portion: {fmtUsd(Math.abs(stateTaxDelta))}</div>
        </div>
        <div className="stat">
          <div className="label">Ending net worth difference</div>
          <div className={`value${nwDelta >= 0 ? '' : ' negative'}`}>
            {nwDelta >= 0 ? '+' : '−'}{fmtCompactUsd(Math.abs(nwDelta))}
          </div>
          <div className="context">at the end of the plan, from taxes and compounding</div>
        </div>
      </div>
      <p className="disclaimer">
        Tax picture only — housing costs, property tax on a specific home, and cost-of-living
        differences aren't part of this comparison unless you've modeled them in Property and
        Budget.
      </p>
    </section>
  );
}
